import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Typography,
  TextField,
  Box,
  CircularProgress,
} from "@mui/material";
import translate from "i18n/translate";
import { connect } from "react-redux";
import { LoadingButton } from "@mui/lab";
import Modal from "components/common/Modal";
import { useIntl } from "react-intl";
import Toast from "components/common/Toast";
import { Notify } from "notiflix";
import ApiRequest from "../../actions/ApiRequest";
import SetHomePageButton from "../common/SetHomePageButton";
import useIsMobile from "shared/hooks/useIsMobile";

const backgroundStyle = {
  backgroundColor: "#fff",
  width: 200,
  height: 200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 16,
  marginBottom: 16,
};

const selectedWalletStyle = {
  border: "1px dotted ",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
  transform: "scale(1.05)",
};

const unselectedWalletStyle = {
  filter: "blur(3px)",
  opacity: 0.7,
};

const REFLECT_IDENTIFIERS = ["reflect"];

// Reflect transfers are capped at 500 (other wallets allow up to 2000).
const REFLECT_MAX_AMOUNT = 500;
const DEFAULT_MAX_AMOUNT = 2000;
const MIN_AMOUNT = 50;

const isReflectWallet = (wallet) => {
  if (!wallet || typeof wallet !== "object") {
    return false;
  }

  const candidates = [
    wallet.product,
    wallet.provider,
    wallet.wallet_name,
    wallet.name,
    wallet.type,
  ];

  return candidates.some(
    (value) =>
      typeof value === "string" &&
      REFLECT_IDENTIFIERS.includes(value.trim().toLowerCase())
  );
};

// Resolve the Reflect fee from the admin-configured commission tiers fetched
// from the backend. The commission is the total fee added on top of the
// transfer and is split 50/50 between the system and the seller. Returns null
// when the amount is invalid or the tiers are not available.
const calculateReflectFees = (amount, tiers) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return null;
  }

  if (!Array.isArray(tiers) || tiers.length === 0) {
    return null;
  }

  let totalFee = null;
  for (const tier of tiers) {
    if (
      numericAmount >= Number(tier.min_amount) &&
      numericAmount <= Number(tier.max_amount)
    ) {
      totalFee = Number(tier.commission);
      break;
    }
  }

  // Amount above the highest configured range -> use the last (MAX) tier
  if (totalFee === null) {
    totalFee = Number(tiers[tiers.length - 1].commission);
  }

  if (!Number.isFinite(totalFee)) {
    return null;
  }

  return {
    totalFee,
    systemFee: totalFee / 2,
    sellerFee: totalFee / 2,
  };
};

const Wallet = ({ isDarktheme }) => {
  const intl = useIntl(); // Use hook instead of direct import
  const IS_MOBILE_DISPLAY = useIsMobile();

  const [name, setName] = useState("");
  const [walletNumber, setWalletNumber] = useState("");
  const [confirmWalletNumber, setConfirmWalletNumber] = useState("");
  const [amount, setAmount] = useState();
  const [openModal, setOpenModal] = useState(false);
  const [sendIngDataLoading, setSendIngDataLoading] = useState(false);
  const [isWalletNameFocused, setIsWalletNameFocused] = useState(true);
  const [modalMessage, setModalMessage] = useState("");
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reflectTiers, setReflectTiers] = useState([]);
  const refInput = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest.post(`get_active_wallets`);
        setWallets(response.data);
        console.log(response.data[0]);
        setIsLoading(false);
      } catch (error) {
        Notify.failure(error.message);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Load the admin-configured Reflect commission tiers so the confirmation
    // dialog shows the same fee the backend will charge. Non-fatal on failure.
    const fetchReflectTiers = async () => {
      try {
        const response = await ApiRequest.post(
          "reflect_commission?operation=GET"
        );
        if (response?.data?.status === "success") {
          setReflectTiers(response.data.data || []);
        }
      } catch (error) {
        console.error("Failed to load Reflect commission tiers:", error);
      }
    };

    fetchReflectTiers();
  }, []);

  useEffect(() => {
    if (refInput.current) {
      refInput.current.focus();
    }
  }, [selectedWallet]);

  const handleWalletNumberChange = useCallback((event) => {
    setWalletNumber(event.target.value);
  }, []);

  const handleConfirmWalletNumberChange = useCallback((event) => {
    setConfirmWalletNumber(event.target.value);
  }, []);
  
  const handleOnConfirm = useCallback(async () => {
    setSendIngDataLoading(true);
    try {
      const response = await ApiRequest.post(
        `wallet_send_money?wallet_id=${selectedWallet.id}&wallet_number=${walletNumber}&amount=${amount}&name=${name}`
      );
      if (response.data.status === "failed") {
        throw new Error(response.data.message);
      }
      setSendIngDataLoading(false);
      setWalletNumber("");
      setConfirmWalletNumber("");
      setAmount();
      Toast.fire({
        icon: "success",
        title: intl.formatMessage({
          id: "Your request has been sent successfully",
        }),
      });
    } catch (error) {
      Notify.failure(error.message);
      Toast.fire({
        icon: "error",
        title: intl.formatMessage({ id: "Your request has been failed" }),
      });
    } finally {
      setSendIngDataLoading(false);
    }
    setOpenModal(false);
  }, [amount, name, selectedWallet?.id, walletNumber, intl]);
  
  const walletNumbersMatch = useMemo(
    () => walletNumber === confirmWalletNumber,
    [walletNumber, confirmWalletNumber]
  );

  const isReflectSelected = useMemo(
    () => isReflectWallet(selectedWallet),
    [selectedWallet]
  );

  const maxAmount = isReflectSelected ? REFLECT_MAX_AMOUNT : DEFAULT_MAX_AMOUNT;

  const isSubmitDisabled = useMemo(
    () =>
      !walletNumbersMatch ||
      walletNumber === "" ||
      amount < MIN_AMOUNT ||
      amount > maxAmount ||
      name.trim().length === 0,
    [walletNumbersMatch, walletNumber, amount, name, maxAmount]
  );
  
  const showConfirmError = useMemo(
    () =>
      [...walletNumber].slice(0, confirmWalletNumber.length).join("") !==
      confirmWalletNumber,
    [walletNumber, confirmWalletNumber]
  );
  
  const handleSubmit = useCallback(() => {
    setOpenModal(true);

    const highlight = (value) =>
      `<span style="font-weight: bold; font-size: 1.2em;">${value}</span>`;

    if (isReflectWallet(selectedWallet)) {
      const fees = calculateReflectFees(amount, reflectTiers);

      if (fees) {
        const formattedMessage = intl.formatMessage(
          { id: "reflect_confirmation_msg_wallet" },
          {
            walletNumber: highlight(walletNumber),
            amount: highlight(amount),
            totalFee: highlight(
              intl.formatNumber(fees.totalFee, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            ),
            systemFee: highlight(
              intl.formatNumber(fees.systemFee, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            ),
            sellerFee: highlight(
              intl.formatNumber(fees.sellerFee, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            ),
          }
        );
        setModalMessage(formattedMessage);
        return;
      }
    }

    const formattedMessage = intl.formatMessage(
      { id: "confirmation_msg_wallet" },
      {
        walletNumber: highlight(walletNumber),
        amount: highlight(amount),
      }
    );
    setModalMessage(formattedMessage);
  }, [amount, walletNumber, intl, selectedWallet, reflectTiers]);

  return (
    <div>
      <div className="card card-home mb-4">
        <div className={!isDarktheme ? "card p-4" : "dark-card p-4"}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h1 className="header-text">{translate("select_wallet")}</h1>
            <SetHomePageButton 
              currentPath="/wallet"
              pageName="Wallet"
              variant="icon"
            />
          </div>
        </div>
      </div>
      {isLoading ? (
        <Box
          sx={{ display: "flex", position: "fixed", left: "50%", top: "40%" }}
        >
          <CircularProgress />
        </Box>
      ) : wallets.length > 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {wallets.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                setSelectedWallet(item);
              }}
              style={{
                ...backgroundStyle,
                ...(selectedWallet && selectedWallet.id === item.id
                  ? selectedWalletStyle
                  : {}),
              }}
            >
              <img
                src={item["logo_url"]}
                alt="wallet"
                style={{
                  borderRadius: 16,
                  cursor: "pointer",
                  ...(selectedWallet && selectedWallet.id !== item.id
                    ? unselectedWalletStyle
                    : {}),
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <div>no data</div>
      )}

      {selectedWallet && (
        <div style={{ width: IS_MOBILE_DISPLAY ? 300 : 400, margin: "0 auto" }}>
          <TextField
            label={translate("Full Name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            fullWidth
            autoFocus
            helperText={translate("Enter your name")}
            onFocus={() => setIsWalletNameFocused(true)}
            inputRef={refInput}
          />
          <TextField
            label={translate("Mobile Number")}
            value={walletNumber}
            onChange={handleWalletNumberChange}
            margin="normal"
            fullWidth
            inputProps={{
              maxLength: 18,
              onCopy: (e) => e.preventDefault(),
            }}
            style={{
              filter: !isWalletNameFocused ? "blur(3px)" : "none",
              direction: "ltr",
            }}
            helperText={translate("Enter mobile number")}
          />

          <TextField
            label={translate("Confirm mobile Number")}
            value={confirmWalletNumber}
            onChange={handleConfirmWalletNumberChange}
            margin="normal"
            fullWidth
            error={showConfirmError}
            helperText={
              showConfirmError ? translate("Numbers do not match") : ""
            }
            onFocus={() => setIsWalletNameFocused(false)}
            onBlur={() => setIsWalletNameFocused(true)}
            style={{
              direction: "ltr",
            }}
          />

          <TextField
            label={translate("amount_you_want_to_charge")}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            fullWidth
            margin="normal"
            inputProps={{
              min: MIN_AMOUNT, // Minimum amount
              max: maxAmount, // Maximum amount (500 for Reflect, 2000 otherwise)
            }}
            error={amount > maxAmount}
            helperText={
              amount > maxAmount
                ? intl
                    .formatMessage({ id: "amount_exceeds_max" })
                    .replace("{max}", maxAmount)
                : intl
                    .formatMessage({ id: "amount_helper_text" })
                    .replace("{min}", MIN_AMOUNT)
                    .replace("{max}", maxAmount)
            }
          />

          <Alert severity="warning" sx={{ marginTop: 2, marginBottom: 2 }}>
            <Typography variant="body1" style={{ fontWeight: "bold" }}>
              {translate("imei_confirmation_warning")}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            disabled={isSubmitDisabled}
            sx={{ marginTop: 2 }}
            onClick={handleSubmit}
          >
            {translate("submit")}
          </Button>
        </div>
      )}

      {openModal && (
        <Modal
          visible={true}
          onClose={() => setOpenModal(false)}
          width={"45vw"}
          title={intl.formatMessage({ id: "Charge The Wallet" })}
        >
          <div style={{ ...backgroundStyle, margin: "0 auto" }}>
            <img
              src={selectedWallet["logo_url"]}
              alt="wallet"
              style={{
                borderRadius: 16,
              }}
            />
          </div>
          <Typography dangerouslySetInnerHTML={{ __html: modalMessage }} />
          <div
            className="d-flex justify-content-end align-items-center mt-4"
            style={{ width: "100%" }}
          >
            <button
              className="btn btn-danger mx-3"
              onClick={() => setOpenModal(false)}
            >
              {translate("Cancel")}
            </button>
            <LoadingButton
              onClick={handleOnConfirm}
              loading={sendIngDataLoading}
              variant="contained"
            >
              {translate("Confirm")}
            </LoadingButton>
          </div>
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = ({ darkTheme: { isDarktheme } }) => ({
  isDarktheme,
});

export default connect(mapStateToProps)(Wallet);
